import {createSelector} from 'reselect'
import isEqual from 'lodash/isEqual.js'
import uniq from 'lodash/uniq.js'
import isEmpty from 'lodash/isEmpty.js'
import omitBy from 'lodash/omitBy.js'

import {
  getState,
  setForm,
  updateForm,
  removeForm,
  formsSelector,
} from '../../store.js'
import {
  DEFAULT_EXPAND_MODE,
  DEFAULT_LINE_SORT,
  DEFAULT_PER_PAGE,
  IS_LINE_COUNTED_FILTER,
  IS_LINE_RECONCILED_FILTER,
  IS_LINE_STARTED_FILTER,
  STOCKTAKE_LINE_SORT_OPTIONS,
  STOCKTAKE_LINE_TROOLEAN_FILTERS,
  STOCKTAKE_URI_COMPONENT,
} from '../../common/constants/Stocktakes.js'
import {showGlobalError} from '../../ordoro/GlobalErrorMessage.js'
import {stocktakeSelector, ensureStocktake} from '../../data/stocktakes.js'
import {locationSelector} from '../../redux/selectors/ui/location.js'
import {navigate} from '../../common/location.js'
import {
  API_PRODUCT_ACTIVE,
  API_PRODUCT_DROPSHIP,
  API_PRODUCT_INVENTORY_SYNC_SUSPENDED,
  API_PRODUCT_KIT,
  API_PRODUCT_KIT_COMPONENT,
  API_PRODUCT_LOW_INVENTORY_AOH,
  API_PRODUCT_LOW_INVENTORY_POH,
  API_PRODUCT_MANUFACTURED,
  API_PRODUCT_MANUFACTURING_COMPONENT,
  API_PRODUCT_NEEDS_SYNC,
  API_PRODUCT_OVERSOLD,
  API_PRODUCT_REORDERED,
  API_PRODUCT_TO_BE_SHIPPED,
  IS_PRODUCT_FBA,
  IS_PRODUCT_FBM,
  PRODUCT_FULFILLMENT_CHANNELS,
} from '../../common/constants/Products.js'
import {cleanProductQuery} from '../ProductListPage/productListFunctions.js'
import {
  ensureArray,
  ensureArrayOfNumbers,
  ensureArrayOrUndefined,
  ensureTroolean,
  nullParamCast,
} from '../../common/ensure.js'
import {TAG_FILTER_OPTIONS, TAG_FILTER_OR} from '../../common/constants/Tags.js'
import apiverson from '../../common/apiverson.js'
import {STOCKTAKE_DETAIL_PAGE} from '../../common/constants/Pages.js'
import {currentPageSelector} from '../../redux/selectors/ui/index.js'
import {buildPath} from '../../common/querystring.js'
import handleListSelection from '../../common/handleListSelection.js'
import {debouncedSaveUserSettings, userSettingSelector} from '../../data/me.js'
import {
  EXPAND_MODE_COLLAPSED,
  EXPAND_MODE_EXPANDED,
} from '../../common/components/List/ExpandAllButton.js'

export const STOCKTAKE_DETAIL_FORM = 'stocktake_detail'
const STOCKTAKE__DETAIL__LIMIT = 'stocktake.detail.limit'
const STOCKTAKE__DETAIL__SORT = 'stocktake.detail.sort'
const STOCKTAKE__DETAIL__EXPAND_MODE = 'stocktake.detail.expand_mode'

export function stocktakeDetailFormSelector(state) {
  return formsSelector(state)[STOCKTAKE_DETAIL_FORM]
}

export const stocktakeDetailReferenceIDSelector = createSelector(
  locationSelector,
  ({pathComponents}) => {
    if (pathComponents[0] === STOCKTAKE_URI_COMPONENT && pathComponents[1]) {
      return pathComponents[1]
    }

    return null
  },
)

export function defaultHashParamsSelector(state) {
  const limit = userSettingSelector(state, {
    setting: STOCKTAKE__DETAIL__LIMIT,
    defaultValue: DEFAULT_PER_PAGE,
  })
  const sort = userSettingSelector(state, {
    setting: STOCKTAKE__DETAIL__SORT,
    defaultValue: DEFAULT_LINE_SORT,
  })

  return {
    limit: limit !== DEFAULT_PER_PAGE ? limit : undefined,
    sort: sort !== DEFAULT_LINE_SORT ? sort : undefined,
  }
}

export function stocktakeDetailURLSelector(state, {referenceID, query}) {
  const defaultQuery = defaultHashParamsSelector(state)

  const pathComponents = [STOCKTAKE_URI_COMPONENT, referenceID]

  return (
    '#' +
    buildPath(pathComponents, {
      ...defaultQuery,
      ...query,
    })
  )
}

export function navigateToStocktakeDetailPage(referenceID, query) {
  const defaultQuery = defaultHashParamsSelector(getState())

  const pathComponents = [STOCKTAKE_URI_COMPONENT, referenceID]

  return navigate(pathComponents, {
    ...defaultQuery,
    ...query,
  })
}

export function navigateStocktakeDetail(updates = {}, referenceID) {
  let {pathComponents, query} = locationSelector(getState())

  if (!(STOCKTAKE_URI_COMPONENT === pathComponents[0] && pathComponents[1])) {
    return
  }

  if (!referenceID) {
    referenceID = pathComponents[1]
  }

  pathComponents = [STOCKTAKE_URI_COMPONENT, referenceID]

  query = {...query, ...updates}

  return navigate(pathComponents, query)
}

export function setupStocktakeDetailForm() {
  setForm(STOCKTAKE_DETAIL_FORM, {
    isLoading: true,
    isLoadingList: true,
    count: 0,
    lineMap: new Map(),
    selectedSKUs: [],
  })
}

export function updateStocktakeDetailForm(updates, meta) {
  updateForm(STOCKTAKE_DETAIL_FORM, updates, meta)
}

export function removeStocktakeDetailForm() {
  removeForm(STOCKTAKE_DETAIL_FORM)
}

export const isLoadingListSelector = createSelector(
  stocktakeDetailFormSelector,
  ({isLoadingList} = {}) => isLoadingList || false,
)

export const skuListSelector = createSelector(
  stocktakeDetailFormSelector,
  ({lineMap} = {}) => [...(lineMap?.keys() || [])],
)

export const countSelector = createSelector(
  stocktakeDetailFormSelector,
  ({count} = {}) => count || 0,
)

export function expandModeSelector(state) {
  return userSettingSelector(state, {
    setting: STOCKTAKE__DETAIL__EXPAND_MODE,
    defaultValue: DEFAULT_EXPAND_MODE,
  })
}

export const expandedSKUsSelector = createSelector(
  stocktakeDetailFormSelector,
  ({expandedSKUs} = {}) => expandedSKUs || [],
)

export function isExpandedSelector(state, {sku}) {
  const expandedSKUs = expandedSKUsSelector(state)
  const expandMode = expandModeSelector(state)

  if (expandMode === EXPAND_MODE_COLLAPSED) {
    return expandedSKUs.includes(sku)
  }
  if (expandMode === EXPAND_MODE_EXPANDED) {
    return !expandedSKUs.includes(sku)
  }
  return false
}

export const selectedSKUsSelector = createSelector(
  stocktakeDetailFormSelector,
  ({selectedSKUs} = {}) => selectedSKUs || [],
)

export const allSelectedSelector = createSelector(
  skuListSelector,
  selectedSKUsSelector,
  (skuList, selected) =>
    skuList.length > 0 && skuList.length === selected.length,
)

export const indeterminateSelectedSelector = createSelector(
  allSelectedSelector,
  selectedSKUsSelector,
  (allSelected, selectedSKUs) => !allSelected && selectedSKUs.length > 0,
)

export function stocktakeDetailQuerySelector(state) {
  stocktakeDetailQuerySelector.cache = stocktakeDetailQuerySelector.cache || {}
  const cache = stocktakeDetailQuerySelector.cache

  const location = locationSelector(state)

  if (location === cache.location) {
    return cache.result
  }

  cache.location = location

  const {query} = location

  const result = {
    search: location.query.search || '',
    sort: location.query.sort || DEFAULT_LINE_SORT,
    limit: Number(location.query.limit) || DEFAULT_PER_PAGE,
    offset: Number(location.query.offset) || 0,
    started: ensureTroolean(query[IS_LINE_STARTED_FILTER]),
    counted: ensureTroolean(query[IS_LINE_COUNTED_FILTER]),
    reconciled: ensureTroolean(query[IS_LINE_RECONCILED_FILTER]),
    category: query.category || '',
    active: ensureTroolean(query[API_PRODUCT_ACTIVE]),
    kit: ensureTroolean(query[API_PRODUCT_KIT]),
    kit_component: ensureTroolean(query[API_PRODUCT_KIT_COMPONENT]),
    manufactured: ensureTroolean(query[API_PRODUCT_MANUFACTURED]),
    manufacturing_component: ensureTroolean(
      query[API_PRODUCT_MANUFACTURING_COMPONENT],
    ),
    dropship: ensureTroolean(query[API_PRODUCT_DROPSHIP]),
    needs_sync: ensureTroolean(query[API_PRODUCT_NEEDS_SYNC]),
    to_be_shipped: ensureTroolean(query[API_PRODUCT_TO_BE_SHIPPED]),
    oversold: ensureTroolean(query[API_PRODUCT_OVERSOLD]),
    reordered: ensureTroolean(query[API_PRODUCT_REORDERED]),
    inventory_sync_suspended: ensureTroolean(
      query[API_PRODUCT_INVENTORY_SYNC_SUSPENDED],
    ),
    low_inventory_aoh: ensureTroolean(query[API_PRODUCT_LOW_INVENTORY_AOH]),
    low_inventory_poh: ensureTroolean(query[API_PRODUCT_LOW_INVENTORY_POH]),
    fba: ensureTroolean(query[IS_PRODUCT_FBA]),
    fbm: ensureTroolean(query[IS_PRODUCT_FBM]),
    supplier: ensureArrayOfNumbers(query.supplier),
    sales_channel: ensureArrayOfNumbers(query.sales_channel),
    tag: ensureArray(query.tag),
    untagged: ensureTroolean(query.untagged) || false,
    tag_filter_by: query.tag_filter_by || TAG_FILTER_OR,
    exclude_tags: ensureArray(query.exclude_tags),
  }

  if (isEqual(cache.result, result)) {
    return cache.result
  }

  cache.result = result

  return result
}

export function cleanStocktakeDetailQuery(query) {
  return {
    [IS_LINE_STARTED_FILTER]: nullParamCast(query[IS_LINE_STARTED_FILTER]),
    [IS_LINE_COUNTED_FILTER]: nullParamCast(query[IS_LINE_COUNTED_FILTER]),
    [IS_LINE_RECONCILED_FILTER]: nullParamCast(
      query[IS_LINE_RECONCILED_FILTER],
    ),
  }
}

export function queryParamsSelector(state) {
  const query = stocktakeDetailQuerySelector(state)

  return {...cleanStocktakeDetailQuery(query), ...cleanProductQuery(query)}
}

export const isFilteredSelector = createSelector(
  queryParamsSelector,
  (queryParams) =>
    !isEmpty(
      omitBy(
        queryParams,
        (value, key) => ['sort', 'limit', 'offset'].includes(key) || !value,
      ),
    ),
)

export function resetOffset() {
  return {
    offset: undefined,
  }
}

export function updateProductFilters(filterUpdates) {
  filterUpdates = Object.entries(filterUpdates).reduce((prev, [key, value]) => {
    if (STOCKTAKE_LINE_TROOLEAN_FILTERS.includes(key)) {
      prev[key] = value === true ? true : value === false ? false : undefined
    }

    return prev
  }, {})

  return {
    ...filterUpdates,
    ...resetOffset(),
  }
}

export function setProductFilters(filterUpdates) {
  return navigateStocktakeDetail(updateProductFilters(filterUpdates))
}

export function updateProductSupplierIDs(supplierIDs) {
  return {
    supplier: ensureArrayOrUndefined(supplierIDs),
    ...resetOffset(),
  }
}

export function setProductSupplierIDs(supplierIDs) {
  return navigateStocktakeDetail(updateProductSupplierIDs(supplierIDs))
}

export function removeProductSupplierID(supplierID) {
  const {supplier} = stocktakeDetailQuerySelector(getState())

  return setProductSupplierIDs(supplier.filter((id) => id !== supplierID))
}

export function updateFulfillmentChannelFilters(filterUpdates) {
  filterUpdates = Object.entries(filterUpdates).reduce((prev, [key, value]) => {
    if (PRODUCT_FULFILLMENT_CHANNELS.includes(key)) {
      prev[key] = value === true ? true : undefined
    }

    return prev
  }, {})

  return {
    ...filterUpdates,
    ...resetOffset(),
  }
}

export function setFulfillmentChannelFilters(filterUpdates) {
  return navigateStocktakeDetail(updateFulfillmentChannelFilters(filterUpdates))
}

export function updateProductCartIDs(cartIDs) {
  return {
    sales_channel: ensureArrayOrUndefined(cartIDs),
    ...resetOffset(),
  }
}

export function setProductCartIDs(cartIDs) {
  return navigateStocktakeDetail(updateProductCartIDs(cartIDs))
}

export function removeProductCartID(cartID) {
  const {sales_channel} = stocktakeDetailQuerySelector(getState())

  return setProductCartIDs(sales_channel.filter((id) => id !== cartID))
}

export function updateCategory(category) {
  return {
    category: (category || '').trim() || undefined,
    ...resetOffset(),
  }
}

export function setCategory(category) {
  return navigateStocktakeDetail(updateCategory(category))
}

export function updateLimit(limit) {
  limit = Number(limit)

  if (![10, 50, 100].includes(limit)) {
    limit = DEFAULT_PER_PAGE
  }

  if (limit === DEFAULT_PER_PAGE) {
    limit = undefined
  }

  return {
    limit,
    ...resetOffset(),
  }
}

export function setLimit(limit) {
  const updates = updateLimit(limit)

  debouncedSaveUserSettings({[STOCKTAKE__DETAIL__LIMIT]: updates.limit})

  return navigateStocktakeDetail(updates)
}

export function updateSort(sort) {
  if (!STOCKTAKE_LINE_SORT_OPTIONS.find(({value}) => sort === value)) {
    sort = DEFAULT_LINE_SORT
  }

  if (sort === DEFAULT_LINE_SORT) {
    sort = undefined
  }

  return {
    sort,
    ...resetOffset(),
  }
}

export function setSort(sort) {
  const updates = updateSort(sort)

  debouncedSaveUserSettings({[STOCKTAKE__DETAIL__SORT]: updates.sort})

  return navigateStocktakeDetail(updates)
}

export function updateSearchText(searchText) {
  return {
    search: (searchText || '').trim() || undefined,
    ...resetOffset(),
  }
}

export function setSearchText(searchText) {
  return navigateStocktakeDetail(updateSearchText(searchText))
}

export function updateProductTagFilters(tags) {
  tags = tags || []

  if (!Array.isArray(tags)) {
    tags = [tags]
  }

  if (tags.length === 0) {
    tags = undefined
  } else {
    tags = uniq(tags)
  }

  return {
    tag: tags,
    ...resetOffset(),
  }
}

export function setProductTagFilters(tags) {
  return navigateStocktakeDetail(updateProductTagFilters(tags))
}

export function removeProductTagFilter(tagName) {
  const {tag} = stocktakeDetailQuerySelector(getState())

  return setProductTagFilters(tag.filter((name) => name !== tagName))
}

export function updateProductUntagged(untagged) {
  return {
    untagged: untagged ? true : undefined,
    ...resetOffset(),
  }
}

export function setProductUntagged(untagged) {
  return navigateStocktakeDetail(updateProductUntagged(untagged))
}

export function updateProductTagFilterBy(tag_filter_by) {
  if (!TAG_FILTER_OPTIONS.find(({value}) => tag_filter_by === value)) {
    tag_filter_by = TAG_FILTER_OR
  }

  if (tag_filter_by === TAG_FILTER_OR) {
    tag_filter_by = undefined
  }

  return {
    tag_filter_by,
    ...resetOffset(),
  }
}

export function setProductTagFilterBy(tag_filter_by) {
  return navigateStocktakeDetail(updateProductTagFilterBy(tag_filter_by))
}

export function updateProductExcludeTags(exclude_tags) {
  return {
    exclude_tags: uniq(ensureArrayOrUndefined(exclude_tags)),
    ...resetOffset(),
  }
}

export function setProductExcludeTags(exclude_tags) {
  return navigateStocktakeDetail(updateProductExcludeTags(exclude_tags))
}

export function removeProductExcludeTag(tagName) {
  const {exclude_tags} = stocktakeDetailQuerySelector(getState())

  return setProductExcludeTags(exclude_tags.filter((name) => name !== tagName))
}

export function updateOffset(offset) {
  offset = Number(offset) || 0

  if (!offset) {
    offset = undefined
  }

  return {
    offset,
  }
}

export function setOffset(offset) {
  return navigateStocktakeDetail(updateOffset(offset))
}

export function setExpandedSKUs(expandedSKUs) {
  updateStocktakeDetailForm({
    expandedSKUs,
  })
}

export function setExpandMode(expandMode) {
  debouncedSaveUserSettings({[STOCKTAKE__DETAIL__EXPAND_MODE]: expandMode})
}

function setQueryResults({count, limit, offset, stocktake_line}) {
  updateStocktakeDetailForm({
    lineMap: new Map(stocktake_line.map((line) => [line.product.sku, line])),
    count,
    currentPage: offset / limit + 1,
    selectedSKUs: [],
    expandedSKUs: [],
  })
}

export function selectStocktakeLine(sku, isSelected, isShiftKey) {
  const selectedSKUs = selectedSKUsSelector(getState())
  const skuList = skuListSelector(getState())

  setSelectedSKUs(
    handleListSelection({
      value: sku,
      isSelected,
      isShiftKey,
      selected: selectedSKUs,
      list: skuList,
    }),
  )
}

export function setSelectedSKUs(selectedSKUs) {
  updateStocktakeDetailForm({
    selectedSKUs,
  })
}

export async function stocktakeDetailToggleExpanded(sku) {
  const expandedSKUs = expandedSKUsSelector(getState())

  const skus = expandedSKUs.filter((p) => p !== sku)

  if (!expandedSKUs.includes(sku)) {
    skus.push(sku)
  }

  setExpandedSKUs(skus)
}

export async function refreshStocktakeDetails() {
  await getCurrentDetailStocktake()
  await refreshStocktakeLines()
}

export async function refreshStocktakeLines() {
  try {
    const currentPage = currentPageSelector(getState())
    if (currentPage !== STOCKTAKE_DETAIL_PAGE) {
      return
    }

    const token = (refreshStocktakeLines.requestToken = {})

    updateStocktakeDetailForm({isLoadingList: true})

    const referenceID = stocktakeDetailReferenceIDSelector(getState())
    const params = queryParamsSelector(getState())

    const {json} = await apiverson.get(
      buildPath(['stocktake', referenceID, 'line'], params),
    )

    if (token !== refreshStocktakeLines.requestToken) {
      return
    }

    setQueryResults(json)

    updateStocktakeDetailForm({isLoadingList: false})
  } catch (err) {
    showGlobalError(
      {
        summary: 'Error querying stocktake lines.',
        details: err.message || err.error_message,
      },
      err,
    )

    updateStocktakeDetailForm({isLoadingList: false})
  }
}

export async function getCurrentDetailStocktake() {
  const referenceID = stocktakeDetailReferenceIDSelector(getState())

  try {
    if (!referenceID) {
      return
    }

    const stocktake = stocktakeSelector(getState(), {referenceID})

    updateStocktakeDetailForm({isLoading: !stocktake})

    await ensureStocktake(referenceID, {reload: true})
  } catch (err) {
    if (!err.response || err.response.status !== 404) {
      showGlobalError(
        {
          summary: `There was an error loading Stocktake Details for ${referenceID}.`,
          details: err.message,
        },
        err,
      )
    }
  }

  updateStocktakeDetailForm({isLoading: false})
}
